import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Modal, Form, Spinner, Table,
} from 'react-bootstrap';

import {
  jtaService, JtaRegion, CreateJtaRegionRequest,
} from '../../../components/Jta/services/jtaService';
import { showConfirm, showError, showSuccess } from '../../../Utilities/sweetAlert';

const emptyForm: CreateJtaRegionRequest = { name: '', sortOrder: 0 };

const JtaRegions: React.FC = () => {
  const [regions, setRegions] = useState<JtaRegion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<JtaRegion | null>(null);
  const [form, setForm] = useState<CreateJtaRegionRequest>(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadRegions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await jtaService.getRegions();
      setRegions(response.data.regions);
    } catch (err) {
      showError('Failed to load JTA regions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRegions();
  }, [loadRegions]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', sortOrder: regions.length + 1 });
    setShowModal(true);
  };

  const openEdit = (region: JtaRegion) => {
    setEditing(region);
    setForm({ name: region.name, sortOrder: region.sortOrder });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showError('Region name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await jtaService.updateRegion(editing.id, form);
        showSuccess('Region updated successfully!');
      } else {
        await jtaService.createRegion(form);
        showSuccess('Region created successfully!');
      }
      setShowModal(false);
      setForm(emptyForm);
      setEditing(null);
      await loadRegions();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to save region');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (region: JtaRegion) => {
    const confirmed = await showConfirm(
      `Delete "${region.name}"?`,
      'Trails in this region will keep their data but lose the region tag.',
      'Delete',
      'Cancel',
    );
    if (!confirmed.isConfirmed) return;
    try {
      await jtaService.deleteRegion(region.id);
      showSuccess('Region deleted');
      await loadRegions();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to delete region');
    }
  };

  return (
    <Container className="py-4">
      <Row className="align-items-center mb-4">
        <Col>
          <h2 className="mb-0">JTA Regions</h2>
          <small className="text-muted">Manage the regions used to group Jordan Trail Association trails.</small>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={openCreate}>+ Add Region</Button>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Order</th>
                  <th>Name</th>
                  <th style={{ width: 180 }} className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {regions.length === 0 ? (
                  <tr><td colSpan={3} className="text-center text-muted py-4">No regions yet.</td></tr>
                ) : (
                  regions.map((region) => (
                    <tr key={region.id}>
                      <td>{region.sortOrder}</td>
                      <td>{region.name}</td>
                      <td className="text-end">
                        <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => openEdit(region)}>Edit</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => handleDelete(region)}>Delete</Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit Region' : 'Add Region'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Petra & Wadi Musa"
                autoFocus
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Sort order</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={form.sortOrder ?? 0}
                onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
              />
              <Form.Text className="text-muted">Lower numbers appear first in the app.</Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default JtaRegions;
